/**
 * GA4 `purchase` event for the donation form.
 *
 * Pushes a GA4 ecommerce payload to the dataLayer once Formie confirms a
 * successful submission. GTM is injected by SEOmatic; pushing into
 * `window.dataLayer` works whether or not the container has loaded yet, since
 * GTM replays anything already queued in the array.
 */
import { readField } from './donation-formie-fields'

const FORM_HANDLE = 'donationForm'

// Matches the Stripe payment field's `currencyFixed`.
const CURRENCY = 'AUD'

const toNumber = (value) => {
    const number = Number.parseFloat(String(value ?? '').replace(/[^0-9.-]/g, ''))

    return Number.isFinite(number) ? number : 0
}

/**
 * Read the cart while the form is still filled in - Formie clears the fields
 * once it swaps in the success message, so this cannot wait until after.
 */
const snapshot = (form) => {
    // 'once' / 'monthly', so one-off and recurring stay separable in reporting
    // without needing a second event type.
    const frequency = readField(form, 'donationFrequency') || 'once'
    const donation = toNumber(readField(form, 'donationAmount'))
    const products = toNumber(readField(form, 'productsPurchasedAmount'))
    const items = []

    if (donation > 0) {
        items.push({
            item_id: `donation-${frequency}`,
            item_name: 'Donation',
            item_category: 'Donation',
            item_variant: frequency,
            price: donation,
            quantity: 1,
        })
    }

    if (products > 0) {
        items.push({
            item_id: 'donation-products',
            item_name: readField(form, 'productsPurchased') || 'Products',
            item_category: 'Product',
            price: products,
            quantity: 1,
        })
    }

    // `totalAmount` is the Calculations field Stripe actually charges
    // (the payment field's amount is `{field:totalAmount}`).
    return { value: toNumber(readField(form, 'totalAmount')), frequency, items }
}

document.querySelectorAll(`[data-form-handle="${FORM_HANDLE}"]`).forEach((form) => {
    let pending = null

    form.addEventListener('onBeforeFormieSubmit', () => {
        pending = snapshot(form)
    })

    form.addEventListener('onAfterFormieSubmit', (event) => {
        const detail = event.detail ?? {}
        const data = detail.data ?? detail

        // Multi-page forms fire this per page; only the final step is a purchase.
        if (data.nextPageId) {
            return
        }

        if (!pending?.value) {
            return
        }

        window.dataLayer = window.dataLayer || []

        // Null the previous ecommerce object first, so items from an earlier
        // event cannot leak into this one (GA4's documented reset).
        window.dataLayer.push({ ecommerce: null })

        window.dataLayer.push({
            event: 'purchase',
            ecommerce: {
                transaction_id: String(data.submissionId ?? data.submissionUid ?? `donation-${Date.now()}`),
                value: pending.value,
                currency: CURRENCY,
                items: pending.items,
            },
        })

        pending = null
    })
})
